import Modal from "features/shared/components/modal";
import ModalHeader from "features/shared/components/modals/modalHeader";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import {
    Container,
    FormContainer,
    ModalContainer,
    StyledImage,
} from "./subProductEditModal.styled";
import LabelWrapper from "features/shared/components/form/components/labelWrapper";
import SubmitButton from "features/shared/components/form/components/submitButton";
import BasicInput from "features/shared/components/form/components/basicInput";
import React, { useState, ChangeEvent, useCallback, useMemo } from "react";
import { initialTagSuggestions } from "features/product/constants/form";
import useSubmit from "features/shared/components/form/hooks/useSubmit";
import { SubProductAction } from "features/product/types/actions.subproduct";
import { verifySubProductForm } from "features/product/functions/form";
import { PropertyType, SubProduct } from "features/product/types/subproduct";
import TagSelect from "../../../tagSelect/tagSelect";
import useFilteredInitialSuggestions from "../../../tagSelect/hooks/useFilteredInitialSuggestions";
import SelectInput from "features/shared/components/form/components/selectInput";
import { SelectionValue } from "features/shared/types/select";
import { emptySubProduct } from "features/product/constants/templates";
import { ISubProductModal } from "features/product/types/modals";
import useCurrentTableState from "../../hooks/useCurrentTableState";

export enum SubProductEditMode {
    Add = "Add",
    Edit = "Edit",
}

interface Props {
    onCloseHandler: () => void;
}

const SubProductEditModal: React.FC<Props & ISubProductModal["params"]> = ({
    onCloseHandler,
    mode,
    idx,
}) => {
    const { readOnlyMode } = useSelector((state) => state.subProducts);
    const dispatch = useDispatch();

    const { subProducts, rows } = useCurrentTableState();

    const originalFields = subProducts[rows[idx as number]];

    const [fields, setFields] = useState<SubProduct>(
        originalFields ?? {
            ...emptySubProduct,
        }
    );

    const [filteredInitialSuggestions] = useFilteredInitialSuggestions({
        unfilteredInitialSuggestions: initialTagSuggestions,
    });
    const [handleSubmit] = useSubmit({
        fields,
        readOnlyMode,
        handleFormVerification: async (fields) => {
            return await verifySubProductForm({
                fields,
                rows,
                originalId: rows[idx as number] ?? "",
            });
        },
        onSubmitCallback: (fields) => {
            dispatch({
                type: SubProductAction.ASSIGN_SUBPRODUCT,
                subProduct: fields,
            });
            onCloseHandler();
        },
    });

    const handleRawChange = (e: ChangeEvent<HTMLInputElement>) => {
        handleChange(e.target.name, e.target.value);
    };

    // Basic one handles normal inputs
    const handleChange = (name: string, value: string) => {
        if (readOnlyMode) return;

        let clonedFields = {
            ...fields,
        };

        switch (name) {
            case "id":
                clonedFields.id = value;
                break;
            case "name:en":
                clonedFields.name = {
                    en: value,
                    th: clonedFields.name?.th || "",
                };
                break;
            case "name:th":
                clonedFields.name = {
                    en: clonedFields.name?.en || "",
                    th: value,
                };
                break;
        }

        setFields(clonedFields);
    };

    const handleImageChange = useCallback(
        (imageUrl: string) => {
            setFields({
                ...fields,
                image: imageUrl,
            });
        },
        [fields]
    );

    const imagePropertySelectionOptions: SelectionValue<string>[] = useMemo(
        () =>
            Object.values(subProducts[rows[idx as number]]?.properties ?? {})
                .filter(
                    ({ type, value }, idx, arr) =>
                        type === PropertyType.Image &&
                        arr.findIndex(
                            ({ value: matchingValue }) =>
                                matchingValue === value
                        ) === idx
                )
                .map(({ value }, idx) => ({
                    display: `Option ${idx + 1}`,
                    value: value as string,
                })),
        [idx, rows, subProducts]
    );

    return (
        <Modal id="subProductPropertyEdit" onCloseHandler={onCloseHandler}>
            <ModalContainer>
                <ModalHeader
                    headingText={`${readOnlyMode ? "View" : mode} subproduct`}
                    onCloseHandler={onCloseHandler}
                />
                <FormContainer onSubmit={handleSubmit}>
                    <Container>
                        <LabelWrapper label="Image Shown">
                            <StyledImage src={fields.image} alt="" />
                        </LabelWrapper>
                        <LabelWrapper
                            label={
                                readOnlyMode
                                    ? "Selected Image Option"
                                    : "Select Image Shown"
                            }
                        >
                            <SelectInput
                                value={fields.image ?? ""}
                                selectionOptions={[
                                    { display: "No Image", value: "" },
                                    ...imagePropertySelectionOptions,
                                ]}
                                onSelectHandler={handleImageChange}
                                readOnlyMode={readOnlyMode}
                            />
                        </LabelWrapper>
                        <LabelWrapper label="subproduct ID">
                            <BasicInput
                                name="id"
                                onChangeHandler={handleRawChange}
                                value={fields.id}
                                disabled={originalFields !== undefined}
                            />
                        </LabelWrapper>
                        <LabelWrapper label="Name (EN)">
                            <BasicInput
                                name="name:en"
                                onChangeHandler={handleRawChange}
                                value={fields.name.en}
                            />
                        </LabelWrapper>
                        <LabelWrapper label="Name (TH)">
                            <BasicInput
                                name="name:th"
                                onChangeHandler={handleRawChange}
                                value={fields.name.th}
                            />
                        </LabelWrapper>
                        <LabelWrapper label="Tags">
                            <TagSelect
                                onChangeHandler={(tags) =>
                                    setFields({ ...fields, tags })
                                }
                                value={fields.tags}
                                initialSuggestions={filteredInitialSuggestions}
                                readOnlyMode={readOnlyMode}
                            />
                        </LabelWrapper>
                    </Container>
                    {!readOnlyMode && <SubmitButton text="Confirm" />}
                </FormContainer>
            </ModalContainer>
        </Modal>
    );
};

export default SubProductEditModal;
